import { Container } from "@mui/material";
import { useCallback, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { Inbox } from "../../../../api/types/inboxesTypes";
import useClientContext from "../../../../context/useClientContext";
import AgentInboxChannelContainer from "./AgentInboxChannelContainer";
import AgentInboxSettingsContainer from "./AgentInboxSettingsContainer";
import { AgentInboxSettingsContextProvider } from "./AgentInboxSettingsContext";
import AgentInboxSettingsPageHeader from "./AgentInboxSettingsPageHeader";
import type { EditInboxChannelState } from "./editInboxChannelState";
import { startEditingChannelAction, validateInboxChannel } from "./editInboxChannelState";

const AgentInboxSettingsPage = () => {
  usePageTitle("Agent Inbox Settings");

  const { hasPermissions } = useClientContext();
  const [searchParams] = useSearchParams();

  const [editedChannelState, setEditedChannelState] = useState<EditInboxChannelState>();

  const { id } = useParams();

  const getInbox = useCallback(() => adminApi.getInbox(id ?? ""), [id]);

  const [inbox, fetchInboxError, { setData: setInbox }] = useFetch(getInbox, {
    onSetData: (data) => {
      const channelId = searchParams.get("channel");
      const channel = data.channels.find((c) => c.id === channelId);
      if (channel) {
        setEditedChannelState(startEditingChannelAction(channel));
      }
    },
  });

  const [agents, fetchAgentConfigurationsError] = useFetch(adminApi.getAgents);

  if (!id) {
    return <Navigate to="/" />;
  }

  const fetchError = fetchInboxError || fetchAgentConfigurationsError;
  if (fetchError) {
    logError(fetchError, "[AgentInboxSettingsPage] fetch");
    return <DataLoadingFailed />;
  }

  if (inbox === undefined || agents === undefined) {
    return <InlineLoader />;
  }

  const handleInboxUpdated = (updatedInbox: Inbox) => {
    setInbox(updatedInbox);
  };

  const hasEditPermissions = hasPermissions(["ManageInboxes"]);

  const editedChannelValidationResults =
    editedChannelState === undefined ? undefined : validateInboxChannel(editedChannelState.channelForm);

  return (
    <AgentInboxSettingsContextProvider
      hasEditPermissions={hasEditPermissions}
      inbox={inbox}
      onInboxUpdated={handleInboxUpdated}
      editedChannelState={editedChannelState}
      setEditedChannelState={setEditedChannelState}
      editedChannelValidationResults={editedChannelValidationResults}
      agents={agents}
    >
      <AgentInboxSettingsPageHeader />

      <ScrollableFlexContainer>
        <Container maxWidth="md" disableGutters sx={{ flex: 1 }}>
          {editedChannelState === undefined && <AgentInboxSettingsContainer />}
          {editedChannelState !== undefined && <AgentInboxChannelContainer />}
        </Container>
      </ScrollableFlexContainer>
    </AgentInboxSettingsContextProvider>
  );
};

export default AgentInboxSettingsPage;
